import { Input, SwitchFormComponent } from 'commons/components';
import { PocBanner } from 'commons/components/components-wrapper/poc-banner.component';
import { PocTooltip } from 'commons/components/components-wrapper/poc-tooltip.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import type { PocInputValidity } from 'commons/types';
import { useTranslation } from 'core/i18n/i18n';
import {
    LOT_NUMBER_MAX_LENGTH_MAX,
    LOT_NUMBER_MAX_LENGTH_MIN,
    MaterialDefinitionFormState,
} from 'modules/settings/pods/settings-materials/settings-material-definitions-form/settings-material-definitions-form.model';
import { MaterialDefinitionFormCard } from 'modules/settings/pods/settings-materials/settings-material-definitions-form/settings-material-definitions-form.style';
import { FormErrors } from 'modules/settings/pods/settings-materials/settings-material-definitions-form/settings-material-definitions-form.validation';
import { LotRulesBannerSlot, LotRulesSection } from './lot-rules-card.style';

interface LotRulesCardProps {
    formState: MaterialDefinitionFormState;
    onFieldChange: (
        field: keyof MaterialDefinitionFormState,
        value: MaterialDefinitionFormState[keyof MaterialDefinitionFormState]
    ) => void;
    onValidity?: (field: string, validity: PocInputValidity) => void;
    errors: FormErrors;
    readOnly: boolean;
}

export const LotRulesCard: React.FC<LotRulesCardProps> = ({
    formState,
    onFieldChange,
    onValidity,
    errors,
    readOnly,
}) => {
    const { t } = useTranslation();

    const handleLotNumberMaxLengthChange = (_field: string, value: string | null) => {
        if (!value) {
            onFieldChange('lotNumberMaxLength', null);
            return;
        }
        const parsed = Number(value);
        onFieldChange('lotNumberMaxLength', Number.isNaN(parsed) ? null : parsed);
    };

    return (
        <MaterialDefinitionFormCard>
            <PocTypography variant="subtitle1">
                {t('settings.materials.material-definitions.form.lot-rules.section-title')}
            </PocTypography>
            <LotRulesSection>
                {readOnly && (
                    <LotRulesBannerSlot>
                        <PocBanner status="info">
                            {t('settings.materials.material-definitions.form.lot-rules.read-only-banner')}
                        </PocBanner>
                    </LotRulesBannerSlot>
                )}
                <Input
                    id="lot-number-max-length"
                    name="lotNumberMaxLength"
                    type="number"
                    label="settings.materials.material-definitions.form.lot-rules.fields.max-length"
                    placeholder="settings.materials.material-definitions.form.lot-rules.fields.max-length-placeholder"
                    value={formState.lotNumberMaxLength === null ? '' : String(formState.lotNumberMaxLength)}
                    minValue={LOT_NUMBER_MAX_LENGTH_MIN}
                    maxValue={LOT_NUMBER_MAX_LENGTH_MAX}
                    required
                    spaceForError
                    disabled={readOnly}
                    validity={errors.lotNumberMaxLength ? { state: 'error', type: ['custom'] } : undefined}
                    validationMessages={
                        errors.lotNumberMaxLength ? { error: { custom: t(errors.lotNumberMaxLength) } } : undefined
                    }
                    onChange={handleLotNumberMaxLengthChange}
                    onValidity={onValidity}
                />
                {readOnly && (
                    <PocTooltip anchor="lot-number-max-length" data-testid="lot-rules-read-only-tooltip">
                        {t('settings.materials.material-definitions.form.lot-rules.read-only-tooltip')}
                    </PocTooltip>
                )}
                <div id="allow-alphanumeric-lot-numbers">
                    <SwitchFormComponent
                        data-testid="switch-allow-alphanumeric-lot-numbers"
                        label={
                            <PocTypography variant="subtitle2">
                                {t('settings.materials.material-definitions.form.lot-rules.fields.allow-alphanumeric')}
                            </PocTypography>
                        }
                        enabled={formState.allowAlphanumericLotNumbers}
                        disabled={readOnly}
                        onChange={(checked) => onFieldChange('allowAlphanumericLotNumbers', checked)}
                    />
                </div>
                {readOnly && (
                    <PocTooltip anchor="allow-alphanumeric-lot-numbers" data-testid="lot-rules-read-only-tooltip">
                        {t('settings.materials.material-definitions.form.lot-rules.read-only-tooltip')}
                    </PocTooltip>
                )}
            </LotRulesSection>
        </MaterialDefinitionFormCard>
    );
};
